import type { Dtos } from "@inrev/common";
import { Option } from "@inrev/core";
import { cn } from "@portal/components/lib/utils";
import { FC } from "react";
import { ChartDataPoint, FsrTrendChart } from "./FsrTrendChart";
import { experianFsrExclusionMessage } from "./utils/experianFsrExclusionMessage";
import { getFsrRiskBand } from "./utils/getFsrRiskBand";
import { getFsrRiskLabel } from "./utils/getFsrRiskLabel";

type Report = Dtos.Admin.SuretyAccount.Get.Response["companies"][number]["creditReports"][number];

type FsrPanelProps = {
	report: Report;
};

export const FsrPanel: FC<FsrPanelProps> = ({ report }) => {
	const fsr = report.creditProfile?.experianFsr;
	const exclusionMessage = experianFsrExclusionMessage(report);
	const band = getFsrRiskBand(fsr);
	const chronological = [...(report.creditProfile?.experianFsrTrends ?? [])].reverse();
	const chartData = chronological.slice(-12).filter((t) => Option.isSome(t.score));
	const chartScores = chartData.map((d) => d.score).filter(Option.isSome);
	const delta =
		chartScores.length >= 2 ? chartScores[chartScores.length - 1] - chartScores[0] : null;

	const factors = (report.creditProfile?.experianFsrFactors ?? []).slice(0, 4);

	return (
		<div
			className="space-y-4 border-b border-bc-default pb-5"
			data-amp-mask
			data-testid="company_credit_fsr_panel"
		>
			<div className="flex flex-wrap items-start justify-between gap-4">
				{Option.isSome(fsr) && <FsrScore fsr={fsr} band={band} />}
				{chartData.length > 0 && <FsrTrend data={chartData} delta={delta} />}
			</div>
			{factors.length > 0 && <ScoreDriversList factors={factors} />}
			{Option.isNone(fsr) && Option.isSome(exclusionMessage) && (
				<div className="text-tertiary text-[14px] italic">{exclusionMessage}</div>
			)}
			{chartData.length === 0 &&
				factors.length === 0 &&
				Option.isNone(fsr) &&
				Option.isNone(exclusionMessage) && (
					<div className="text-tertiary text-[14px] italic">No FSR data on file.</div>
				)}
		</div>
	);
};

type FsrScoreProps = {
	fsr: number;
	band: "low" | "medium" | "high" | null;
};

const FsrScore: FC<FsrScoreProps> = ({ fsr, band }) => {
	return (
		<div className="flex flex-col gap-2">
			<span className="text-tertiary text-[11px] font-medium uppercase tracking-wide">
				Experian FSR
			</span>
			<div className="flex items-baseline gap-2">
				<span
					className={cn(
						"text-[64px] font-semibold leading-none",
						band === "low" && "text-confirm",
						band === "medium" && "text-intent-warning-text",
						band === "high" && "text-danger",
						band === null && "text-primary",
					)}
				>
					{fsr}
				</span>
				<span className="text-tertiary text-[14px]">/ 100</span>
			</div>
			{Option.isSome(band) && (
				<div className="flex items-center gap-2 text-[14px]">
					<span
						className={cn(
							"h-2 w-2 rounded-full",
							band === "low" && "bg-confirm",
							band === "medium" && "bg-warning",
							band === "high" && "bg-danger",
						)}
						aria-hidden
					/>
					<span
						className={cn(
							band === "low" && "text-confirm",
							band === "medium" && "text-intent-warning-text",
							band === "high" && "text-danger",
						)}
					>
						{getFsrRiskLabel(band)}
					</span>
				</div>
			)}
		</div>
	);
};

type FsrTrendProps = {
	data: ChartDataPoint[];
	delta: number | null;
};

const FsrTrend: FC<FsrTrendProps> = ({ data, delta }) => {
	return (
		<div className="flex min-w-[200px] max-w-[360px] flex-1 flex-col gap-2">
			<div className="flex items-center justify-between gap-2">
				<span className="text-tertiary text-[11px] font-medium uppercase tracking-wide">
					{data.length}-Quarter trend
				</span>
				{Option.isSome(delta) && delta !== 0 && (
					<span
						className={cn("text-[12px] font-medium", delta > 0 ? "text-confirm" : "text-danger")}
					>
						{delta > 0 ? "↑" : "↓"} {Math.abs(delta)} pts
					</span>
				)}
			</div>
			<div className="h-[120px] w-full overflow-hidden">
				<FsrTrendChart data={data} delta={delta} />
			</div>
		</div>
	);
};

type ScoreDriversListProps = {
	factors: NonNullable<
		NonNullable<
			Dtos.Admin.SuretyAccount.Get.Response["companies"][number]["creditReports"][number]["creditProfile"]
		>["experianFsrFactors"]
	>;
};

const ScoreDriversList: FC<ScoreDriversListProps> = ({ factors }) => {
	if (factors.length === 0) return null;

	return (
		<div className="space-y-2">
			<div className="text-tertiary text-[11px] font-medium uppercase tracking-wide">
				Score Drivers{" "}
				<span className="normal-case font-normal tracking-normal">— in order of impact</span>
			</div>
			<ul className="list-none space-y-2 pl-0">
				{factors.map((factor, index) => (
					<li
						key={`${factor.code}-${index}`}
						className="flex items-baseline gap-2.5 text-[13px] text-primary"
					>
						<span className="bg-surface-neutral text-tertiary shrink-0 rounded px-1.5 py-0.5 text-[11px] font-medium tabular-nums">
							F{index + 1}
						</span>
						<span>{factor.definition ?? factor.code}</span>
					</li>
				))}
			</ul>
		</div>
	);
};
